/**
 * How many lives a revived entry comes back with (ISSUE-1326). Defaults to
 * one, the legacy one-shot revive. Minimum one; the ceiling is the contest's
 * own lives setting, so a Commissioner cannot hand back more lives than the
 * contest ever gives anyone.
 *
 * Local to this feature, beside `PickRequirementStepper`, for the same reason:
 * one consumer today.
 */
import { Button } from "@sportsmet/ui";
import type { JSX } from "react";

import { livesGrantedHelp, livesGrantedLabel } from "./reviveSheetModel";

export type typeLivesGrantedStepperProps = {
  name: string;
  value: number;
  maxLives: number | null;
  onChange: (next: number) => void;
  disabled?: boolean | undefined;
};

export function LivesGrantedStepper(
  props: typeLivesGrantedStepperProps,
): JSX.Element {
  const { name, value, maxLives, onChange, disabled = false } = props;
  const isAtMax = maxLives !== null && value >= maxLives;

  return (
    <div className="flex flex-col gap-2" data-testid="revive-lives">
      <span className="text-body-strong text-ink">
        {livesGrantedLabel({ name })}
      </span>
      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          size="icon"
          aria-label="Fewer lives"
          data-testid="revive-lives-decrement"
          disabled={disabled || value <= 1}
          onClick={() => {
            onChange(value - 1);
          }}
        >
          -
        </Button>
        <span
          data-testid="revive-lives-value"
          aria-live="polite"
          className="min-w-8 text-center text-body-strong text-ink"
        >
          {value}
        </span>
        <Button
          variant="secondary"
          size="icon"
          aria-label="More lives"
          data-testid="revive-lives-increment"
          disabled={disabled || isAtMax}
          onClick={() => {
            onChange(value + 1);
          }}
        >
          +
        </Button>
      </div>
      <p className="text-caption text-ink-secondary">
        {livesGrantedHelp({ maxLives })}
      </p>
    </div>
  );
}
